import "./ExpenseRefundFolder.css";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  buildQueryString,
  checkRoles,
  checkSendInApprove,
  showError,
  showSuccess,
  trimData,
} from "../../../utils/utilities";
import { setFilters } from "../../../redux/ducks/Filters";
import {
  dbDate,
  dbDateHour,
  expenseRefundTypes,
  expenseRefundViewing,
  langDate,
  langDateHour,
  paginationDefaults,
} from "../../../utils/properties";
import { cloneDeep, get, includes, isEmpty, last, remove, split, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { Badge, Col, Descriptions, Modal, Row, Select, Skeleton, Spin, Steps, Table, Tooltip } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  FileOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { useLocation, useParams } from "react-router-dom";
import ExpenseRefund from "../../../services/api/ExpenseRefund";
import ExpenseRefundFolder from "../../../services/api/ExpenseRefundFolder";
import CreditCardApi from "../../../services/api/CreditCard";
import { setLoading } from "../../../redux/ducks/Loading";
import { setSection } from "../../../redux/ducks/Section";
import UploadFile from "../components/UploadFile";
import Justification from "../components/form/Justification";
import FlatRate from "../components/form/FlatRate";
import Km from "../components/form/Km";
import CreditCard from "../components/form/CreditCard";
import ExpenseRefundApproval from "../../../services/api/ExpenseRefundApproval";
import SaveOrApprove from "../components/SaveOrApprove";
import dayjs from "dayjs";

const { confirm } = Modal;
const { Option } = Select;

const ExpenseRefundFolderDetail = ({ showAll = false }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const currentLanguage = useSelector((state) =>
    state.languageDuck?.currentLanguage === null ? "GB" : get(state.languageDuck, "currentLanguage", "GB")
  );
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const currentMonth = dayjs().format("YYYYMM");
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const dateFormat = langDate[toLower(currentLanguage)];
  const dateHourFormat = langDateHour[toLower(currentLanguage)];
  const pageIsLoading = useSelector((state) => get(state.loadingDuck, "pageIsLoading", false));
  const [state, setState] = useState({
    lastMonthClosed: currentMonth,
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    tableContent: [],
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
    cdcList: [],
    prjList: [],
    detailFolder: {},
    typologySelected: null,
    expenseRefundTypes: [],
    selectedRows: [],
    canEdit: false,
  });
  const [stateSteps, setStateSteps] = useState(0);
  const [fileList, setFileList] = useState({ receipt: [], payment_tracking: [] });
  const [uploading, setUploading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    filters.obj.page = 0; // reset current page to zero when entering or refreshing the page
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string, true);
  }, []);

  useEffect(() => {
    dispatch(
      setSection({
        section: {
          title: params.folderId === "new-expense" ? "expenseRefundFolderDetail.newExpense" : state.detailFolder.note,
        },
      })
    );
  }, [state.detailFolder]);

  const formikExpenseReport = useFormik({
    initialValues: {
      amount: get(state.modal.data, "amount", null),
      invoicePfp: get(state.modal.data, "invoicePfp", false),
      flight: get(state.modal.data, "flight", false),
      abroad: get(state.modal.data, "abroad", false),
      privateCar: get(state.modal.data, "privateCar", false),
      docDate: get(state.modal.data, "docDate", null) !== null ? dayjs(state.modal.data.docDate, dbDateHour) : null,
      arrivalDate:
        get(state.modal.data, "arrivalDate", null) !== null ? dayjs(state.modal.data.arrivalDate, dbDateHour) : null,
      departureDate:
        get(state.modal.data, "departureDate", null) !== null
          ? dayjs(state.modal.data.departureDate, dbDateHour)
          : null,
      docNumber: get(state.modal.data, "docNumber", null),
      expenseAccountType: get(state.modal.data, "expenseAccountType.id", null),
      locationDeparture: get(state.modal.data, "locationDeparture.id", null),
      locationArrival: get(state.modal.data, "locationArrival.id", null),
      locationDepartureDesc: get(state.modal.data, "locationDepartureDesc", null),
      locationArrivalDesc: get(state.modal.data, "locationArrivalDesc", null),
      departure: get(state.modal.data, "departure", null),
      arrival: get(state.modal.data, "arrival", null),
      fuelCard: get(state.modal.data, "fuelCard", null),
      note: get(state.modal.data, "note", null),
      supplierName: get(state.modal.data, "supplierName", null),
      supplierVat: get(state.modal.data, "supplierVat", null),
      carPlate: get(state.modal.data, "carPlate", null),
      totalKm: get(state.modal.data, "totalKm", null),
      carFuelRefund: get(state.modal.data, "carFuelRefund.id", null),
      creditCard: get(state.modal.data, "creditCard", null),
      parkAuto: get(state.modal.data, "parkAuto", null),
      traceablePayment: get(state.modal.data, "traceablePayment", false),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      switch (state.typologySelected) {
        case "justification":
          if (!values.amount) {
            errors.amount = "Required!";
          }
          if (values.invoicePfp && !values.supplierName) {
            errors.supplierName = "Required!";
          }
          if (values.invoicePfp && !values.supplierVat) {
            errors.supplierVat = "Required!";
          }
          if (!values.docDate) {
            errors.docDate = "Required!";
          }
          if (!values.docNumber) {
            errors.docNumber = "Required!";
          }
          if (!values.expenseAccountType) {
            errors.expenseAccountType = "Required!";
          }
          if (!values.note) {
            errors.note = "Required!";
          }
          break;
        case "flat_rate":
          if (values.flight && !values.locationDeparture) {
            errors.locationDeparture = "Required!";
          }
          if (values.flight && !values.locationArrival) {
            errors.locationArrival = "Required!";
          }
          if (!values.flight && !values.locationDepartureDesc) {
            errors.locationDepartureDesc = "Required!";
          }
          if (!values.flight && !values.locationArrivalDesc) {
            errors.locationArrivalDesc = "Required!";
          }
          if (!values.departureDate) {
            errors.departureDate = "Required!";
          }
          if (!values.arrivalDate) {
            errors.arrivalDate = "Required!";
          }
          if (!values.note) {
            errors.note = "Required!";
          }
          break;
        case "km":
          if (!values.departure) {
            errors.departure = "Required!";
          }
          if (values.privateCar && !values.arrival) {
            errors.arrival = "Required!";
          }
          if (!values.docDate) {
            errors.docDate = "Required!";
          }
          if (values.privateCar && !values.docNumber) {
            errors.docNumber = "Required!";
          }
          if (values.privateCar && !values.carPlate) {
            errors.carPlate = "Required!";
          }
          if (!values.totalKm) {
            errors.totalKm = "Required!";
          }
          if (values.privateCar && !values.carFuelRefund) {
            errors.carFuelRefund = "Required!";
          }
          if (!values.privateCar && !values.amount && values.amount !== 0) {
            errors.amount = "Required!";
          }
          if (!values.privateCar && !values.parkAuto) {
            errors.parkAuto = "Required!";
          }
          if (!values.note) {
            errors.note = "Required!";
          }
          break;
        case "credit_card":
          if (!values.amount) {
            errors.amount = "Required!";
          }
          if (values.invoicePfp && !values.docNumber) {
            errors.docNumber = "Required!";
          }
          if (!values.expenseAccountType) {
            errors.expenseAccountType = "Required!";
          }
          if (!values.note) {
            errors.note = "Required!";
          }
          if (values.invoicePfp && !values.supplierName) {
            errors.supplierName = "Required!";
          }
          if (values.invoicePfp && !values.supplierVat) {
            errors.supplierVat = "Required!";
          }
          if (!values.docDate) {
            errors.docDate = "Required!";
          }
          break;
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      let dataSave = {};
      if (state.modal.data?.id) {
        // edit
        dataSave = state.modal.data;
      } else {
        //new
        dataSave["status"] = "PLANNING";
        dataSave["type"] = state.typologySelected;
        dataSave["expenseRefundFolder"] = { id: state.detailFolder.id };
      }
      Object.keys(values).forEach((key) => {
        let dataKey = values[key];
        if (dayjs.isDayjs(dataKey)) {
          dataKey = dayjs(dataKey, dateHourFormat).format(dbDateHour);
        }
        if (
          includes(
            [
              "expenseAccountType",
              "locationArrival",
              "locationDeparture",
              "carFuelRefund",
              "creditCard",
              "parkAuto",
              "fuelCard",
            ],
            key
          )
        ) {
          if (values[key]) {
            dataKey = { id: values[key] };
          }
        }
        dataSave[key] = dataKey;
      });
      let saveResponse = { error: true };
      switch (state.typologySelected) {
        case "justification":
          saveResponse = await ExpenseRefund.setExpenseRefundJustification(dataSave);
          break;
        case "flat_rate":
          saveResponse = await ExpenseRefund.setExpenseRefundFlatRate(dataSave);
          break;
        case "km":
          saveResponse = await ExpenseRefund.setExpenseRefundKm(dataSave);
          break;
        case "credit_card":
          saveResponse = await ExpenseRefund.setExpenseRefundCreditCard(dataSave);
          break;
      }
      if (!saveResponse.error) {
        showSuccess();
        setStateSteps(stateSteps + 1);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          modal: { visible: true, type: "edit", data: saveResponse.data },
        }));
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
        setState((prevState) => ({ ...prevState, loading: false }));
      }
    },
  });

  const getModalData = async () => {
    let manageExpenseRefundTypes = cloneDeep(expenseRefundTypes);
    setState((prevState) => ({ ...prevState, loading: true }));
    const { data, error } = await CreditCardApi.getCreditCards(
      `allPages=true&user.username=${
        checkRoles(userInfo.roles, ["EXPENSE_REFUND_SUBST"]) ? state.detailFolder.user.username : userInfo.username
      }`
    );
    if (!error && data) {
      if (isEmpty(data)) {
        remove(manageExpenseRefundTypes, (c) => c === "credit_card");
      }
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    if (!state.detailFolder.flatRate) {
      remove(manageExpenseRefundTypes, (f) => f === "flat_rate");
    }
    setState((prevState) => ({
      ...prevState,
      modal: { visible: true, type: "new" },
      loading: false,
      expenseRefundTypes: manageExpenseRefundTypes,
    }));
  };

  const ChooseTypology = () => {
    return (
      <>
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Select
          className="w-100"
          onChange={(e) => setState((prevState) => ({ ...prevState, typologySelected: e }))}
          value={state.typologySelected}
          placeholder={t("expenseRefundFolder.typology")}
        >
          {state.expenseRefundTypes.map((exp) => (
            <Option key={exp} value={exp}>
              {t(`expenseRefundFolder.typologies.${exp}`)}
            </Option>
          ))}
        </Select>
      </>
    );
  };

  const closeModal = () => {
    dataList(filters.string);
    formikExpenseReport.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} }, typologySelected: null }));
    setFileList({ receipt: [], payment_tracking: [] });
    setStateSteps(0);
  };

  const sendInApprove = (ids) => {
    confirm({
      title: t("expenseRefundFolder.sendInApprove"),
      icon: <WarningOutlined />,
      onOk: async () => {
        const { error } = await ExpenseRefundApproval.sendApproval(ids);
        if (!error) {
          setState((prevState) => ({
            ...prevState,
            modal: { visible: false, type: "", data: {} },
            typologySelected: null,
          }));
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string, true);
          showSuccess();
        } else {
          showError(get(error, "messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };
  const steps = [
    <ChooseTypology key={0} />,
    <div key={1}>
      {/**
       * state.canEdit -> check for users with role in visualization
       * state.modal.type !== "visualizer" -> check if user can edit but the status is WAITING APPROVAL or APPROVED
       */}
      {state.typologySelected === "justification" ? (
        <Justification
          canEdit={state.canEdit ? state.modal.type !== "visualizer" : state.canEdit}
          detailFolder={state.detailFolder}
          form={formikExpenseReport}
        />
      ) : state.typologySelected === "flat_rate" ? (
        <FlatRate
          canEdit={state.canEdit ? state.modal.type !== "visualizer" : state.canEdit}
          detailFolder={state.detailFolder}
          form={formikExpenseReport}
          modal={state.modal}
        />
      ) : state.typologySelected === "km" ? (
        <Km
          canEdit={state.canEdit ? state.modal.type !== "visualizer" : state.canEdit}
          detailFolder={state.detailFolder}
          form={formikExpenseReport}
          userInfo={!showAll ? userInfo : state.detailFolder?.user}
        />
      ) : state.typologySelected === "credit_card" ? (
        <CreditCard
          canEdit={state.canEdit ? state.modal.type !== "visualizer" : state.canEdit}
          detailFolder={state.detailFolder}
          form={formikExpenseReport}
        />
      ) : (
        ""
      )}
      {state.modal?.data?.messageApproval && (
        <Row className="mt-1em" justify="space-around">
          <Col span={22}>
            <p>
              <b>{t("expenseRefundFolder.refutedNote")}</b>
              <br />
              {state.modal?.data?.messageApproval}
            </p>
          </Col>
        </Row>
      )}
    </div>,
    <UploadFile
      key={2}
      canEdit={state.canEdit ? state.modal.type !== "visualizer" : state.canEdit}
      uploading={uploading}
      modal={state.modal}
      fileList={fileList}
      setFileList={setFileList}
    />,
    <SaveOrApprove
      key={3}
      canEdit={state.canEdit ? state.modal.type !== "visualizer" : state.canEdit}
      id={state.modal.data?.id}
      closeModal={closeModal}
      sendInApprove={sendInApprove}
    />,
  ];

  const dataList = async (queryString, getDetailFolder = false) => {
    let detailCallback = cloneDeep(state.detailFolder);
    dispatch(setLoading(true));
    if (getDetailFolder) {
      detailCallback = await getDetailCallback(params.folderId);
    }
    queryString =
      queryString !== ""
        ? queryString + `&expenseRefundFolder.id=${params.folderId}`
        : `?expenseRefundFolder.id=${params.folderId}`;
    const { data, error, currentPage, pageSize, totalItems } = await ExpenseRefund.getExpenseRefunds(queryString);
    if (!error && data) {
      let canEdit = true;
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      if (showAll) {
        canEdit =
          checkRoles(userInfo.roles, expenseRefundViewing["expenseRefundFolderUsers"].edit) ||
          checkRoles(userInfo.roles, ["EXPENSE_REFUND_SUBST"])
            ? checkRoles(userInfo.roles, expenseRefundViewing["expenseRefundFolderUsers"].view)
              ? location?.state?.canEdit
                ? true
                : false
              : true
            : false;
      }
      setState((prevState) => ({
        ...prevState,
        tableContent: data,
        detailFolder: detailCallback,
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
        canEdit,
      }));
      setSelectedRowKeys([]);
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleUpload = async () => {
    let checkFilesStep = { receipt: false, payment_tracking: false, showSuccess: false };
    setUploading(true);
    /* Procedo con l'upload dei file
     * Posso avere solo la ricevuta oppure la ricevuta ed il pagamento tracciabile e questo è determinato dalla variabile traceablePayment
     */
    //Controllo se esiste il file di ricevuta
    if (!isEmpty(fileList.receipt) && fileList.receipt[0]?.status !== "done") {
      const formData = new FormData();
      fileList.receipt.forEach((file) => {
        const normFilename = file.name.replace(/[/?<>\\:*|",]/g, "-");
        formData.append("file", file, normFilename);
      });
      formData.append("type", "RECEIPT");
      const response = await ExpenseRefund.uploadDocument(state.modal.data?.id, formData);
      if (response.error) {
        showError(get(response, "error.messageCode", "ER000"));
        checkFilesStep = { ...checkFilesStep, receipt: false };
      } else {
        checkFilesStep = { ...checkFilesStep, receipt: true, showSuccess: true };
      }
    } else {
      // File già caricato o non presente, proseguo
      checkFilesStep = { ...checkFilesStep, receipt: true };
    }
    // Controllo se esiste il file di tracciamento
    if (state.modal.data.traceablePayment) {
      if (!isEmpty(fileList.payment_tracking) && fileList.payment_tracking[0]?.status !== "done") {
        const formDataTracking = new FormData();
        fileList.payment_tracking.forEach((file) => {
          const normFilename = file.name.replace(/[/?<>\\:*|",]/g, "-");
          formDataTracking.append("file", file, normFilename);
        });
        formDataTracking.append("type", "PAYMENT_TRACKING");
        const responseTracking = await ExpenseRefund.uploadDocument(state.modal.data?.id, formDataTracking);
        if (responseTracking.error) {
          showError(get(responseTracking, "error.messageCode", "ER000"));
          checkFilesStep = { ...checkFilesStep, payment_tracking: false };
        } else {
          checkFilesStep = { ...checkFilesStep, payment_tracking: true, showSuccess: true };
        }
      } else {
        // file già caricato o non presente, proseguo
        checkFilesStep = { ...checkFilesStep, payment_tracking: true };
      }
    } else {
      // file non necessario proseguo
      checkFilesStep = { ...checkFilesStep, payment_tracking: true };
    }
    if (checkFilesStep.receipt && checkFilesStep.payment_tracking) {
      if (checkFilesStep.showSuccess) {
        showSuccess();
      }
      setTimeout(() => {
        // Necessario per dare il tempo al sistema di verificare se la nota spesa può essere mandata in approvazione
        setStateSteps(stateSteps + 1);
      }, 600);
    }
    setUploading(false);
  };

  const getDetailCallback = async (id) => {
    let detailFolder = {};
    const { data, error } = await ExpenseRefundFolder.getExpenseRefundFolder(id);
    if (!error && data) {
      detailFolder = data;
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    return detailFolder;
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await ExpenseRefund.deleteExpenseRefund(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const editCallback = async (id, step, visualization) => {
    let typologySelected = null;
    let dataEdit = {};
    setState((prevState) => ({ ...prevState, loading: true }));
    const { data, error } = await ExpenseRefund.getExpenseRefund(id);
    if (!error && data) {
      dataEdit = data;
      typologySelected = toLower(data.type);
      const tmpFileList = { receipt: [], payment_tracking: [] };
      if (!isEmpty(data.documents)) {
        data.documents.forEach((doc) => {
          if (doc.type === "RECEIPT") {
            const parts = split(doc.filePath, "/");
            const fileName = last(parts);
            tmpFileList.receipt.push({ uid: doc.id, name: fileName, status: "done" });
          } else if (doc.type === "PAYMENT_TRACKING") {
            const parts = split(doc.filePath, "/");
            const fileName = last(parts);
            tmpFileList.payment_tracking.push({ uid: doc.id, name: fileName, status: "done" });
          }
        });
      }
      setFileList(tmpFileList);
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({
      ...prevState,
      loading: false,
      modal: { visible: true, type: visualization ? "visualizer" : "edit", data: dataEdit },
      typologySelected,
    }));
    setStateSteps(step);
  };

  const columns = [
    {
      title: t("expenseRefundFolder.table.docDate"),
      key: "date",
      render: (data) =>
        data.docDate
          ? dayjs(data.docDate, dbDate).format(dateFormat)
          : data.departureDate
            ? dayjs(data.departureDate, dbDate).format(dateFormat)
            : "-",
    },
    {
      title: t("expenseRefundFolder.table.typology"),
      key: "type",
      dataIndex: "type",
      render: (type) => t(`expenseRefundFolder.typologies.${toLower(type)}`),
    },
    {
      title: t("expenseRefundFolder.table.status"),
      key: "status",
      dataIndex: "status",
      render: (status) => t(`projects.status.${toLower(status)}`),
    },
    {
      title: t("expenseRefundFolder.table.note"),
      key: "note",
      dataIndex: "note",
    },
    {
      title: t("expenseRefundFolder.table.dateSendApproval"),
      key: "dateSendApproval",
      dataIndex: "dateSendApproval",
      render: (date) => (date !== null ? dayjs(date, dbDate).format(dateFormat) : "-"),
    },
    {
      title: t("expenseRefundFolder.table.calcRefund"),
      key: "calcRefund",
      dataIndex: "calcRefund",
      render: (calcRefund) => `${calcRefund || "-"} €`,
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            {data?.status === "REFUTED" && (
              <div>
                <Tooltip title={data?.messageApproval}>
                  <WarningOutlined key={data.id} className="icon-medium PFred noCursorPointer" />
                </Tooltip>
              </div>
            )}
            {data?.status === "APPROVED" && (
              <Button currentIcon={<CheckCircleOutlined className="icon-medium green noCursorPointer" />} type="text" />
            )}
            <Button
              className="ml-10"
              currentIcon={<FileOutlined className="icon-medium" />}
              type="text"
              clickCallback={() => editCallback(data?.id, 2, data?.status !== "PLANNING" && data?.status !== "REFUTED")}
            />
            <Button
              className="ml-10"
              currentIcon={<EditOutlined className="icon-medium" />}
              type="text"
              clickCallback={() => editCallback(data?.id, 1, data?.status !== "PLANNING" && data?.status !== "REFUTED")}
            />
            <Button
              className="ml-10"
              currentIcon={<DeleteOutlined className="icon-medium" />}
              type="text"
              isDisabled={(data?.status !== "PLANNING" && data?.status !== "REFUTED") || !state.canEdit}
              clickCallback={() => deleteCallback(data?.id)}
            />
            <Button
              className="ml-10"
              currentIcon={<CheckOutlined className="icon-medium" />}
              type="text"
              isDisabled={
                !state.canEdit
                  ? true
                  : !isEmpty(data)
                    ? !checkSendInApprove({
                        status: data.status,
                        type: data.type,
                        flight: data.flight,
                        documents: data.documents,
                        traceablePayment: data.traceablePayment,
                      })
                    : true
              }
              clickCallback={() => sendInApprove([{ id: data?.id }])}
            />
          </div>
        );
      },
    },
  ];

  const sendInApproveAll = () => {
    confirm({
      title: t("expenseRefundFolder.sendInApprove"),
      icon: <WarningOutlined />,
      onOk: async () => {
        const { error } = await ExpenseRefundApproval.sendApproval(state.selectedRows);
        if (!error) {
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string, true);
        } else {
          showError(get(error, "messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const nextStep = () => {
    switch (stateSteps) {
      case 0:
        setStateSteps(stateSteps + 1);
        break;
      case 1:
        if (formikExpenseReport.dirty && formikExpenseReport.isValid) {
          formikExpenseReport.submitForm();
        }
        break;
      case 2:
        handleUpload();
        break;
      case 3:
        break;
    }
  };

  const selectRightRow = (keys, selectedRows) => {
    let arr = [];
    selectedRows.forEach((item) => {
      arr.push({ id: item.id });
    });
    setState((prevState) => ({ ...prevState, selectedRows: arr }));
    setSelectedRowKeys(keys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, selectedRows) => selectRightRow(keys, selectedRows),
    getCheckboxProps: (record) => {
      let checkboxProps = {};
      if (!isEmpty(record)) {
        if (
          !checkSendInApprove({
            status: record.status,
            type: record.type,
            flight: record.flight,
            documents: record.documents,
            traceablePayment: record.traceablePayment,
          })
        ) {
          checkboxProps.disabled = true;
        }
      }
      return checkboxProps;
    },
  };

  return (
    <>
      {pageIsLoading ? (
        <Skeleton />
      ) : (
        <Row gutter={[40, 24]} className="mb-10">
          <Col span={24}>
            <Descriptions title={state.detailFolder?.note} bordered>
              <Descriptions.Item label={t("expenseRefundFolder.status")}>
                <Badge
                  status={
                    state.detailFolder?.status === "APPROVED"
                      ? "success"
                      : state.detailFolder?.status === "PLANNING"
                        ? "default"
                        : state.detailFolder?.status === "REFUTED"
                          ? "error"
                          : "warning"
                  }
                  text={state.detailFolder?.status ? t(`expenseRefundFolder.${state.detailFolder?.status}`) : ""}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t("expenseRefundFolder.periodStart")}>
                {state.detailFolder?.periodStart
                  ? dayjs(state.detailFolder.periodStart, dbDate).format(dateFormat)
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("expenseRefundFolder.periodEnd")}>
                {state.detailFolder?.periodEnd ? dayjs(state.detailFolder.periodEnd, dbDate).format(dateFormat) : "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  state.detailFolder?.costCentre !== null
                    ? t("expenseRefundFolder.costCentre")
                    : t("expenseRefundFolder.projectTask")
                }
                span={3}
              >
                {state.detailFolder?.costCentre !== null
                  ? `${state.detailFolder?.costCentre?.id} | ${state.detailFolder?.costCentre?.name}`
                  : `${state.detailFolder?.projectTask?.id} | ${state.detailFolder?.projectTask?.name}`}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      )}
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          className="mr-10"
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={getModalData}
          isDisabled={
            checkRoles(userInfo.roles, ["EXPENSE_REFUND_SUBST"])
              ? checkRoles(userInfo.roles, expenseRefundViewing["expenseRefundFolderUsers"].view)
                ? location?.state?.canEdit
                  ? false
                  : showAll
                : false
              : showAll
          }
        />
      </Row>
      <Table
        rowSelection={rowSelection}
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          className="mr-10"
          currentIcon={<CheckOutlined className="icon-medium" />}
          clickCallback={sendInApproveAll}
          isDisabled={!state.canEdit || isEmpty(state.selectedRows)}
        />
      </Row>
      <Modal
        className="expense-refund-modal"
        open={state.modal.visible}
        width={700}
        title={
          (state.modal.type === "edit" || state.modal.type === "visualizer") &&
          state.modal.data &&
          state.modal.data.note
            ? t(`expenseRefundFolder.typologies.${toLower(state.modal.data.type)}`) + " - " + state.modal.data.note
            : t("expenseRefundFolder.new")
        }
        footer={
          /* [
        <Button
          key="jumpButton"
          currentIcon={<StepForwardOutlined className="icon-medium" />}
          clickCallback={() => setStateSteps(stateSteps + 1)}
          className={stateSteps !== 2 ? "d-none" : ""}
          type="secondary"
          isLoading={uploading}
        />, */
          <Button
            key="okButton"
            currentIcon={<ArrowRightOutlined className="icon-medium" />}
            clickCallback={nextStep}
            isLoading={uploading}
            isDisabled={
              state.canEdit && stateSteps === 0
                ? !state.typologySelected
                : stateSteps === 1
                  ? !(formikExpenseReport.dirty && formikExpenseReport.isValid)
                  : false
            }
            className={stateSteps === 3 ? "d-none ml-10" : "ml-10"}
          />
          /*  ] */
        }
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]} align="middle" justify="center">
          <Col span={24}>
            <Steps
              current={stateSteps}
              items={[
                { title: t("expenseRefundFolder.steps.typology"), disabled: state.modal.type === "edit" },
                { title: t("expenseRefundFolder.steps.data") },
                { title: t("expenseRefundFolder.steps.uploadFile") },
                { title: t("expenseRefundFolder.steps.save") },
              ]}
            />
          </Col>
          <Col span={24}>{steps[stateSteps]}</Col>
        </Row>
      </Modal>
    </>
  );
};

export default ExpenseRefundFolderDetail;
